import { FC, useEffect, useMemo, useState } from 'react';
import {
  percentageValidator,
  getRequiredValidator,
  greaterThanValidator,
  greaterThanOrNullValidator,
} from '@app/core/validations/hook-forms/validators';
import { useFormContext } from 'react-hook-form';
import styles from './opm-inputs.module.scss';
import {
  numericBaseFieldFormattingProps,
  percentageFieldFormattingProps,
} from '@app/shared/components/form-controls/form-field/form-field-patterns';
import { OpmInputDto, ProjectDto } from '@app/shared/models/contracts/project-dto';
import { SelectOption } from '@app/shared/models/option';
import GridTextField from '@app/shared/components/grid-controls/grid-text-field/GridTextField';
import { GridSelectField } from '@app/shared/components/grid-controls/grid-select-field/GridSelectField';
import { Alignment } from '@app/shared/enums/alignment.enum';
import { TABLE_LABELS_COL_WIDTH_OPM_INPUTS } from '@app/shared/constants/table-config';
import { useLocale } from '@app/core/hooks/useLocale';
import { useAppDispatch, useAppSelector } from '@app/core/hooks/redux-hooks';
import {
  CalcMethod,
  OpmInputDataSource,
  OpmSimulatedValue,
  ReduceYearsType,
  StaticCaseSelectionOptions,
} from '@app/shared/models/contracts/enums/shared-enums';
import { cloneToMutableData, enumKeyByValue } from '@app/shared/helpers';
import { OpmInputFormModel2 } from './OpmInputs';
import SvgRevert from '@app/shared/icons/Revert';
import { useOpmInputsFromCapitalStructure } from './utils/useOpmInputsFromCapitalStructure';
import * as projectActions from '@core/store/project-slice';
import { areCaseEventsSame } from '@app/core/inputs/handleOpmCaseSelection';
import { GridFieldTooltipVariation } from '@app/shared/components/grid-controls/grid-field-options';
import { ReduceYearsModal } from '@app/shared/components/modal/reduce-years-modal/ReduceYearsModal';
import useTriggerEffect from '@app/core/hooks/useTriggerEffect';
import {
  selectCapitalStructure,
  selectCapitalStructureId,
  selectCapitalStructures,
} from '@app/core/store/capital-structure-slice-selectors';

interface OpmKeyInputsProps {
  submitData: () => void;
  formValues: OpmInputFormModel2;
  notifyForecastYearsChangeInProgress: (changeInProgress: boolean) => void;
  onCaseSelectionChange?: (newCaseId: string) => void;
}

export const OpmKeyInputs: FC<OpmKeyInputsProps> = ({
  submitData,
  formValues,
  notifyForecastYearsChangeInProgress,
  onCaseSelectionChange,
}) => {
  const project = useAppSelector((state) => state.project?.projectDraft);
  const capitalStructure = useAppSelector(selectCapitalStructure);
  const capitalStructures = useAppSelector(selectCapitalStructures);
  const capitalStructureKey = useAppSelector(selectCapitalStructureId);
  const dispatch = useAppDispatch();
  const [isForecastYearRemovalModalVisible, setIsForecastYearRemovalModalVisible] = useState(false);
  const formMethods = useFormContext<OpmInputFormModel2>();
  const { l } = useLocale();
  const { getEvOrEquityValueFromCapitalStructure } = useOpmInputsFromCapitalStructure();
  const [countOfForecastYears, setCountOfForecastYears] = useState<number>(
    formValues.forecastYears
  );
  const { getValues, trigger } = formMethods;
  const opmInputs = project.opmInput;
  const isPwermAndOpm =
    project.details.calcMethod === enumKeyByValue(CalcMethod, CalcMethod.PWERM_AND_OPM);
  const isOpmOnly = project.details.calcMethod === enumKeyByValue(CalcMethod, CalcMethod.OPM);

  const abbrevOpmValue =
    opmInputs.opmSimulatedValue ===
    enumKeyByValue(OpmSimulatedValue, OpmSimulatedValue.EnterpriseValue)
      ? 'EV'
      : OpmSimulatedValue.Equity;

  const forecastYearsOptions: SelectOption[] = [...Array(10).keys()].map((item) => ({
    viewValue: item + 1,
    value: item + 1,
  }));

  const casesSameEvents = useMemo(() => {
    const currentCapitalStructure = capitalStructures[capitalStructureKey];
    if (!currentCapitalStructure) {
      return true;
    }
    return areCaseEventsSame(project.pwermInput.cases, currentCapitalStructure.eventSets);
  }, [project.pwermInput.cases, capitalStructures, capitalStructureKey]);

  const caseSelectionOptions: SelectOption[] = [...project.pwermInput.cases]
    .sort((a, b) => (a.caseOrder ?? 0) - (b.caseOrder ?? 0))
    .map((caseItem) => ({
      viewValue: caseItem.narrative,
      value: caseItem.caseId,
    }));

  if (casesSameEvents && caseSelectionOptions.length > 1) {
    caseSelectionOptions.unshift({
      viewValue: l('_CaseWeighted'),
      value: StaticCaseSelectionOptions.CaseWeighted,
    });
  }

  useEffect(() => {
    setCountOfForecastYears(formValues.forecastYears);
  }, [formValues.forecastYears]);

  useTriggerEffect(trigger);

  const handleForecastYearQuantityChange = (event?: React.FocusEvent) => {
    if (event) {
      // don't submit the data before we set the state that the modal is open (if applicable)
      event.stopPropagation();
    }
    const updatedForecastYears = getValues().forecastYears;
    notifyForecastYearsChangeInProgress(true);

    if (Number(countOfForecastYears) > Number(updatedForecastYears)) {
      setIsForecastYearRemovalModalVisible(true);
      return;
    } else if (Number(countOfForecastYears) < Number(updatedForecastYears)) {
      updateOpmForecastYears(updatedForecastYears);
    }

    notifyForecastYearsChangeInProgress(false);
  };

  const updateOpmForecastYears = async (updatedCount: number) => {
    const forecastYearsSource = OpmInputDataSource.Override;

    const dividendSetId = project.opmInput.selectedDividendSetId;
    let forecastDividendSet;
    if (dividendSetId != null) {
      forecastDividendSet = cloneToMutableData(
        capitalStructure.forecastDividendSets[dividendSetId]
      );
    }
    if (forecastDividendSet) {
      const numberOfForecastYears = getValues().forecastYears;

      Object.values(forecastDividendSet.instruments).forEach((instrument) => {
        const existingDates = Object.keys(instrument.dates);
        const lastDate = existingDates.length > 0 ? existingDates[existingDates.length - 1] : null;

        if (numberOfForecastYears > existingDates.length && lastDate) {
          for (let i = existingDates.length; i < numberOfForecastYears; i++) {
            const newYearDate = new Date(lastDate);
            newYearDate.setFullYear(newYearDate.getFullYear() + (i - existingDates.length + 1));
            const isoDate = newYearDate.toISOString().slice(0, 10);

            instrument.dates[isoDate] = { totalDividendValue: null };
          }
        }
      });
    }

    const updatedProject: ProjectDto = {
      ...project,
      capitalStructures: {
        ...capitalStructures,
        [capitalStructureKey]: {
          ...capitalStructure,
          forecastDividendSets: {
            ...capitalStructure.forecastDividendSets,
            ...(dividendSetId && forecastDividendSet
              ? { [dividendSetId]: forecastDividendSet }
              : {}),
          },
        },
      },
      opmInput: {
        ...project.opmInput,
        forecastYears: updatedCount,
        forecastYearsSource: forecastYearsSource,
      },
    };
    await dispatch(projectActions.updateProjectDraft({ project: updatedProject }));
  };

  const revertForecastYears = () => {
    setIsForecastYearRemovalModalVisible(false);
    formMethods.setValue('forecastYears', countOfForecastYears);
    notifyForecastYearsChangeInProgress(false);
  };

  const removeForecastYears = async () => {
    const updatedForecastYearsCount = getValues().forecastYears;
    await updateOpmForecastYears(updatedForecastYearsCount);
    setIsForecastYearRemovalModalVisible(false);
    notifyForecastYearsChangeInProgress(false);
  };

  const displayRevert = (rowSource: keyof OpmInputDto) => {
    if (
      (rowSource === 'evValueSource' && (isPwermAndOpm || isOpmOnly)) ||
      (isPwermAndOpm && rowSource !== 'evValueSource')
    ) {
      return opmInputs[rowSource] === OpmInputDataSource.Override;
    }
  };

  const handleRevert = (rowSource: keyof OpmInputDto) => {
    if (rowSource === 'evValue') {
      formMethods.setValue('evValue', getEvOrEquityValueFromCapitalStructure());
      submitData();
    }
  };

  const handleCaseSelectionChange = (_event?: React.FocusEvent) => {
    const selectedCaseId = getValues().selectedCaseId;
    if (selectedCaseId && onCaseSelectionChange) {
      onCaseSelectionChange(selectedCaseId);
    }
  };

  return (
    <div>
      <table
        style={{ marginBottom: '1.6rem' }}
        className={`table-primary table-primary--zebra ${styles[`opm-table-header`]}`}>
        <thead>
          <tr>
            <th
              colSpan={2}
              className={`table-primary__cell--header-tertiary ${styles[`table-title`]}`}>
              {l('_KeyInputs')}
            </th>
          </tr>
        </thead>
        <tbody>
          {isPwermAndOpm && (
            <tr>
              <th className={`table-primary__cell--vertical-separator ${styles[`opm-table-rows`]}`}>
                {l('_CaseSelected', { valuationType: abbrevOpmValue })}
                <sup className={styles['required-field']}>*</sup>
              </th>
              <td>
                <GridSelectField
                  aria-label={l('_SelectorAriaLabel', { label: l('_Case') })}
                  className={`${styles['selector-dropdown']}`}
                  name="selectedCaseId"
                  data-testid="selectedCaseId"
                  options={caseSelectionOptions}
                  rules={{ ...getRequiredValidator() }}
                  onBlur={(event) => handleCaseSelectionChange(event)}
                />
              </td>
            </tr>
          )}
          <tr>
            <th
              style={{ minWidth: `${TABLE_LABELS_COL_WIDTH_OPM_INPUTS}px` }}
              className={`table-primary__cell--vertical-separator ${styles[`opm-table-rows`]}`}>
              {l('_ValuationTypeSpotValue', { valuationType: abbrevOpmValue })}
              <sup className={styles['required-field']}>*</sup>
              {displayRevert('evValueSource') && (
                <SvgRevert
                  // stop the onBlur event occurring so we can control when the data is submitted
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handleRevert('evValue')}
                  className={styles['revert-button']}
                />
              )}
            </th>
            <td>
              <GridTextField
                className={
                  opmInputs.evValueSource === OpmInputDataSource.Override
                    ? styles['revertable-value']
                    : ''
                }
                name="evValue"
                data-testid="evValue"
                alignment={Alignment.Right}
                rules={{ ...getRequiredValidator(), ...greaterThanValidator(0) }}
                {...numericBaseFieldFormattingProps}
              />
            </td>
          </tr>
          <tr>
            <th className={`table-primary__cell--vertical-separator ${styles[`opm-table-rows`]}`}>
              {l('_NumberOfForecastYears')}
              <sup className={styles['required-field']}>*</sup>
            </th>
            <td>
              <GridSelectField
                aria-label={l('_SelectorAriaLabel', { label: l('_Year') })}
                className={`${styles['selector-dropdown']}`}
                name="forecastYears"
                data-testid="forecastYears"
                onBlur={(event) => handleForecastYearQuantityChange(event)}
                options={forecastYearsOptions}
                rules={{ ...getRequiredValidator() }}
              />
            </td>
          </tr>
          <tr>
            <th className={`table-primary__cell--vertical-separator ${styles[`opm-table-rows`]}`}>
              {l('_ValuationTypeVolatility', { valuationType: abbrevOpmValue })}
              <sup className={styles['required-field']}>*</sup>
            </th>
            <td>
              <GridTextField
                name="evVolatility"
                data-testid="evVolatility"
                rules={{
                  ...getRequiredValidator(),
                  ...percentageValidator,
                }}
                {...percentageFieldFormattingProps}
              />
            </td>
          </tr>
          <tr>
            <th className={`table-primary__cell--vertical-separator ${styles[`opm-table-rows`]}`}>
              {l(
                opmInputs.opmSimulatedValue ===
                  enumKeyByValue(OpmSimulatedValue, OpmSimulatedValue.EnterpriseValue)
                  ? '_RealWorldRateWacc'
                  : '_RealWorldRateCostOfEquity'
              )}
            </th>
            <td>
              <GridTextField
                name="realWorldRate"
                data-testid="realWorldRate"
                rules={{
                  ...greaterThanOrNullValidator(0),
                }}
                tooltipVariation={GridFieldTooltipVariation.Top}
                {...percentageFieldFormattingProps}
                alignment={Alignment.Right}
              />
            </td>
          </tr>
        </tbody>
      </table>
      <ReduceYearsModal
        onCloseButtonClick={() => revertForecastYears()}
        onCancel={() => revertForecastYears()}
        onConfirm={() => removeForecastYears()}
        isOpen={isForecastYearRemovalModalVisible}
        type={ReduceYearsType.Forecast}
      />
    </div>
  );
};
